import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";

export const EXPERIENCE_OPTIONS_QUERY_KEY = "experienceOptions";

interface ExperienceOption {
  label: string;
  value: string;
}

interface UseExperienceOptionsResult {
  experienceOptions: ExperienceOption[];
  isLoading: boolean;
  error: Error | null;
}

export const useExperienceOptions = (): UseExperienceOptionsResult => {
  const sessionManagementService = useSessionManagementServiceV1();

  const { companyId } = useParams();

  const {
    data: experienceOptions = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: [EXPERIENCE_OPTIONS_QUERY_KEY, companyId],
    queryFn: () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      return sessionManagementService.getContentMetadataNames(companyId);
    },
    select: (contentMetadataNames) => {
      return contentMetadataNames
        .map((metadata) => ({
          value: metadata.contentMetadataId,
          label: metadata.friendlyName,
        }))
        .filter((option: ExperienceOption) => option.value && option.label)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    enabled: !!companyId,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
  });

  return {
    experienceOptions,
    isLoading,
    error,
  };
};
